import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  platformSettings,
  type PlatformStorageBody,
} from '@/api/platformSettings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Navigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Cloud, HardDrive, CheckCircle2, AlertCircle, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type Provider = 'r2' | 's3';

export function StorageSettings() {
  const me = useCurrentUser();
  if (!me.is_super_admin) return <Navigate to="/dashboard" replace />;
  return <StorageSettingsInner />;
}

function StorageSettingsInner() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['platform-storage'],
    queryFn: () => platformSettings.getStorage(),
  });

  const [editing, setEditing] = useState(false);
  const [provider, setProvider] = useState<Provider>('r2');
  const [accountId, setAccountId] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [region, setRegion] = useState('auto');
  const [bucket, setBucket] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<{
    ok: boolean; error?: string; bucket?: string;
  } | null>(null);

  // When switching provider, set sensible defaults
  useEffect(() => {
    if (provider === 'r2') {
      setRegion('auto');
      if (accountId && !endpoint) {
        setEndpoint(`https://${accountId}.r2.cloudflarestorage.com`);
      }
    } else if (provider === 's3' && region === 'auto') {
      setRegion('us-east-1');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, accountId]);

  const save = useMutation({
    mutationFn: (body: PlatformStorageBody) => platformSettings.putStorage(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-storage'] });
      setEditing(false);
      setSaveError(null);
      setAccessKeyId(''); setSecretAccessKey('');
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    },
  });

  const clear = useMutation({
    mutationFn: () => platformSettings.putStorage({ clear: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-storage'] }),
  });

  const verify = useMutation({
    mutationFn: () => platformSettings.verifyStorage(),
    onSuccess: (r) => setVerifyResult(r),
  });

  function submit() {
    setSaveError(null);
    if (!accessKeyId.trim() || !secretAccessKey.trim() || !endpoint.trim() || !bucket.trim()) {
      setSaveError('All fields except Account ID (S3-only) are required.');
      return;
    }
    save.mutate({
      provider,
      account_id: accountId.trim() || null,
      access_key_id: accessKeyId.trim(),
      secret_access_key: secretAccessKey.trim(),
      endpoint: endpoint.trim(),
      region: region.trim(),
      bucket: bucket.trim(),
    });
  }

  return (
    <>
      <PageHeader
        eyebrow="Platform settings"
        title="Recording storage"
        description="Where caller recordings and voicemail audio live. Cloudflare R2 recommended — zero egress fees. Falls back to local filesystem when unconfigured."
      />
      <div className="max-w-2xl space-y-4">
        {isLoading ? (
          <Card><CardContent className="py-8 text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : (
          <>
            <StatusCard view={data} />
            {editing || !data?.configured ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {data?.configured ? 'Update credentials' : 'Configure credentials'}
                  </CardTitle>
                  <CardDescription>
                    All fields are stored encrypted at rest. You can rotate them at any time.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1">
                    <Label>Provider</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {(['r2', 's3'] as const).map((p) => (
                        <label
                          key={p}
                          className={cn(
                            'cursor-pointer rounded-md border-2 px-4 py-3 transition-all',
                            provider === p
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-slate-400',
                          )}
                        >
                          <input
                            type="radio"
                            name="provider"
                            checked={provider === p}
                            onChange={() => setProvider(p)}
                            className="sr-only"
                          />
                          <div className="flex items-center gap-3">
                            <Cloud className="h-5 w-5 text-primary" />
                            <div>
                              <div className="text-sm font-semibold">
                                {p === 'r2' ? 'Cloudflare R2' : 'AWS S3'}
                              </div>
                              <div className="text-[11px] text-muted-foreground">
                                {p === 'r2' ? 'Zero egress, recommended' : 'Standard S3'}
                              </div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {provider === 'r2' && (
                    <div className="space-y-1">
                      <Label htmlFor="st-acct">Account ID</Label>
                      <Input
                        id="st-acct" value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        placeholder="abc123def456…"
                        className="font-mono"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Top of the R2 overview page in Cloudflare. We&rsquo;ll derive the endpoint automatically.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="st-ak">Access key ID</Label>
                      {/* autoComplete off — keep browsers/password managers
                          from echoing stored credentials into a field that's
                          visible on screen-shares. Matches AwsPollySettings. */}
                      <Input
                        id="st-ak" value={accessKeyId}
                        onChange={(e) => setAccessKeyId(e.target.value)}
                        placeholder={data?.access_key_id_redacted ?? 'paste access key id'}
                        className="font-mono"
                        autoComplete="off" spellCheck={false}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="st-sk">Secret access key</Label>
                      <Input
                        id="st-sk" type="password" value={secretAccessKey}
                        onChange={(e) => setSecretAccessKey(e.target.value)}
                        placeholder="••••••••"
                        className="font-mono"
                        autoComplete="off" spellCheck={false}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="st-ep">Endpoint URL</Label>
                    <Input
                      id="st-ep" value={endpoint}
                      onChange={(e) => setEndpoint(e.target.value)}
                      placeholder={
                        provider === 'r2'
                          ? 'https://<account>.r2.cloudflarestorage.com'
                          : 'https://s3.<region>.amazonaws.com'
                      }
                      className="font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-[10rem_1fr] gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="st-rg">Region</Label>
                      {provider === 'r2' ? (
                        <Input id="st-rg" value="auto" disabled className="font-mono bg-slate-50" />
                      ) : (
                        <Select value={region} onValueChange={setRegion}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[
                              'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
                              'eu-west-1', 'eu-central-1', 'ap-southeast-1', 'ap-southeast-2',
                            ].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="st-bkt">Bucket name</Label>
                      <Input
                        id="st-bkt" value={bucket}
                        onChange={(e) => setBucket(e.target.value)}
                        placeholder="ivrello-recordings-prod"
                        className="font-mono"
                      />
                    </div>
                  </div>

                  {saveError && <p role="alert" className="text-sm text-destructive">{saveError}</p>}

                  <div className="flex items-center gap-2 pt-2">
                    {data?.configured && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => { setEditing(false); setSaveError(null); }}
                        disabled={save.isPending}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button size="sm" onClick={submit} disabled={save.isPending}>
                      {save.isPending ? 'Saving…' : 'Save credentials'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => {
                  setProvider(data!.provider ?? 'r2');
                  setAccountId(data!.account_id ?? '');
                  setEndpoint(data!.endpoint ?? '');
                  setRegion(data!.region ?? 'auto');
                  setBucket(data!.bucket ?? '');
                  setEditing(true);
                  setVerifyResult(null);
                }}>
                  Update credentials
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => { setVerifyResult(null); verify.mutate(); }}
                  disabled={verify.isPending}
                >
                  {verify.isPending
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Verifying…</>
                    : 'Verify connection'}
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => {
                    if (confirm('Clear storage credentials? Recordings will fall back to local filesystem.')) {
                      clear.mutate();
                    }
                  }}
                  disabled={clear.isPending}
                  className="text-destructive hover:text-destructive hover:bg-destructive/5"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Clear
                </Button>
              </div>
            )}

            {verifyResult && (
              <Card className={cn(
                'border-l-4',
                verifyResult.ok ? 'border-l-emerald-500' : 'border-l-rose-500',
              )}>
                <CardContent className="py-4 flex items-start gap-3">
                  {verifyResult.ok ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                  )}
                  <div className="text-sm">
                    <p className="font-medium">
                      {verifyResult.ok
                        ? `Connected to ${verifyResult.bucket}`
                        : 'Verification failed'}
                    </p>
                    {verifyResult.error && (
                      <p className="text-xs text-muted-foreground mt-1 font-mono break-all">
                        {verifyResult.error}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}

function StatusCard({ view }: { view: import('@/api/platformSettings').PlatformStorageView | undefined }) {
  if (!view) return null;
  if (!view.configured) {
    return (
      <Card className="border-l-4 border-l-amber-400">
        <CardContent className="py-4 flex items-start gap-3">
          <HardDrive className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Using local filesystem storage</p>
            <p className="text-xs text-muted-foreground mt-1">
              Recordings are written to disk on the backend server.
              Configure R2 or S3 below to move to cloud storage and enable backups.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-l-4 border-l-emerald-500">
      <CardContent className="py-4 flex items-start gap-3">
        <Cloud className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            Connected to{' '}
            <span className="font-mono">{view.provider === 'r2' ? 'Cloudflare R2' : 'AWS S3'}</span>
          </p>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Bucket</dt>
            <dd className="font-mono">{view.bucket}</dd>
            <dt className="text-muted-foreground">Endpoint</dt>
            <dd className="font-mono truncate">{view.endpoint}</dd>
            <dt className="text-muted-foreground">Region</dt>
            <dd className="font-mono">{view.region}</dd>
            <dt className="text-muted-foreground">Access key</dt>
            <dd className="font-mono">{view.access_key_id_redacted}</dd>
          </dl>
        </div>
      </CardContent>
    </Card>
  );
}
